import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { provinces } from "../data/provinces";
import { regions } from "../data/regions";
import { citiesByProvince } from "../data/cities";
import { getHead } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, CTABand, SectionHead } from "../components/Blocks";

const head = getHead("/service-areas");

export default function ServiceAreas() {
  return (
    <>
      <Seo title={head.title} description={head.description} path="/service-areas" />
      <PageHero
        overline="Coast to Coast"
        title="Security Guard Service Areas Across Canada"
        lead="Maze Security Solutions operates in all 13 Canadian provinces and territories, with licensed guards serving more than 690 cities and communities. Select your province or city for local coverage details."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="provinces-directory">
        <SectionHead overline="Coverage" title="Provinces & Territories" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {provinces.map((p) => (
            <Link
              key={p.slug}
              to={`/security-guards-${p.slug}`}
              data-testid={`areas-province-${p.slug}`}
              className="group flex items-center justify-between border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm p-5 transition-all duration-200 hover:-translate-y-0.5"
            >
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{p.name}</h2>
                <p className="text-xs text-gray-500 mt-1">{citiesByProvince(p.slug).length} cities served</p>
              </div>
              <ChevronRight className="h-5 w-5 text-[#FF5500]" />
            </Link>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="regions-section">
        <SectionHead overline="Regional Programs" title="Multi-City Coverage by Region" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {regions.map((r) => (
            <Link
              key={r.slug}
              to={`/regions/${r.slug}`}
              data-testid={`areas-region-${r.slug}`}
              className="group flex items-center justify-between border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm px-5 py-4 transition-all duration-200"
            >
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{r.name}</span>
              <ChevronRight className="h-4 w-4 text-[#FF5500]" />
            </Link>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="cities-directory">
        <SectionHead overline="Local Pages" title="Security Guards by City" />
        <div className="space-y-8">
          {provinces.map((p) => (
            <div key={p.slug}>
              <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-3">{p.name}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {citiesByProvince(p.slug).map((c) => (
                  <Link
                    key={c.slug}
                    to={`/security-guards-${c.slug}`}
                    data-testid={`areas-city-${c.slug}`}
                    className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors py-1"
                  >
                    <MapPin className="h-3 w-3 text-[#FF5500] shrink-0" /> {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <CTABand title="Don't See Your City?" sub="We deploy beyond our listed cities for the right engagement. Call dispatch and ask — coverage follows demand." />
    </>
  );
}
